// =============================================================================
// palletCompute — the billing-critical tare → net → underweight computation.
//
// Lifted VERBATIM from NewPalletForm (Pallets.tsx) so every path that sets a
// pallet's net_weight_kg / tare_overrides — the single create form, the bulk
// "weigh later" dialog — produces an IDENTICAL result. There is NO server-side
// recompute of net from tare_overrides (mig 869 records the measured tare but
// trusts the client's net), so any divergence here mis-bills silently.
//
// Tare resolution per recipe line:
//   - fixed line: product_recipes.line_tare_* (mig 188) wins; stock_items.tare_*
//     is the fallback. Missing → the line BLOCKS the save (no legacy guess).
//   - variable line (mig 869, stock_items.tare_variable): the operator weighs it
//     at pack/weigh time; the measured per-unit kg comes in via `tareInputs`.
//   - basis: PER_CARTON and PER_LUG both scale with the actual unit count;
//     PER_PALLET is fixed. Variable lines default to PER_PALLET when no basis
//     is set (e.g. a base).
// =============================================================================

// PER_LUG is the local/Freshmark label for a packed unit — those lines are
// counted in lugs, not cartons — and scales IDENTICALLY to PER_CARTON. Keeping
// the two distinct values lets the UI read "per lug" without forking the maths.
export type TareBasis = 'PER_CARTON' | 'PER_PALLET' | 'PER_LUG'

/** True for bases whose tare scales with the operator's actual unit count. */
export const tareScalesWithUnits = (basis: TareBasis | null): boolean =>
  basis === 'PER_CARTON' || basis === 'PER_LUG'

/** Human noun for a basis — drives the "per carton / pallet / lug" labels. */
export const TARE_BASIS_NOUN: Record<TareBasis, string> = {
  PER_CARTON: 'carton',
  PER_PALLET: 'pallet',
  PER_LUG:    'lug',
}

/** What one recipe line takes off a FULL pallet at the scale, in words and kg.
 *  computePalletTotals counts a per-pallet line as qty × tare and a per-carton /
 *  per-lug line as qty × tare × (cartons ÷ standard), so a full pallet loses
 *  qty × tare either way. The rate card showed only the per-unit tare ("1.000 kg
 *  / pallet") beside a qty of 60, so nobody saw Fresca's wrap take 60 kg off
 *  every pallet until the nets looked wrong on the floor (mig 20261312000000).
 *  "per unit" on every basis: qty counts items, which need not be one per box
 *  (2 liners per lug on a 45-lug pallet is qty 90). */
export function lineTareOnPallet(l: { tareKg: number; basis: TareBasis; qty: number }): { kg: number; text: string } {
  const kg = l.qty * l.tareKg
  const pallet = tareScalesWithUnits(l.basis) ? 'per full pallet' : 'per pallet'
  return { kg, text: `${l.tareKg.toFixed(3)} kg per unit × ${l.qty} = ${kg.toFixed(1)} kg ${pallet}` }
}

export interface TareComponent {
  component_id: string
  recipe_units: number
  item:         string
  tare_kg:      number | null
  tare_basis:   TareBasis | null
  is_variable:  boolean
  category:     string | null
}

export interface PalletComputeInput {
  /** units_per_pallet for the picked variant (the carton/lug standard). */
  standard:    number
  components:  TareComponent[]
  cartonQty:   number
  gross:       number
  /** component_id → operator-entered per-unit weight (kg) for variable lines. */
  tareInputs:  Record<string, string>
  /** picked variant's weight floor/target; null when none configured. */
  variant:     { min_weight_per_unit_kg: number | null; target_weight_per_unit_kg: number | null } | null
  /** Optional human label for the rate card / product (e.g. "E10D · CIBEL NOVA"),
   *  woven into the incomplete-recipe blockers so the operator knows WHICH card
   *  to fix. Absent → the generic strings (kept byte-identical for old callers). */
  cardLabel?:  string | null
  /** Does this flow weigh its pallets? (commodity_templates.tracks_pallet_weight)
   *  Default TRUE = the citrus rule, byte-identical for every existing caller.
   *
   *  FALSE only relaxes the case where NOTHING was weighed: with no gross, the
   *  weight and tare blockers are dropped, because tare only exists to turn a
   *  gross into a net and there is no gross to convert. Type a gross on such a
   *  flow and the full chain — tare → net → underweight — runs exactly as it
   *  does for citrus, so an optional weight is never a half-computed one. */
  weightRequired?: boolean
}

export interface PalletComputeResult {
  totalTareKg:            number | null
  netWeight:              number | null
  netPerCarton:          number | null
  /** fixed lines with no tare configured — BLOCK (ask admin to finish rate card). */
  missingTareLines:       string[]
  /** variable lines not yet weighed — BLOCK (operator owes a measurement). */
  unenteredVariableLines: string[]
  isUnderweight:          boolean
  minPerUnit:             number | null
  targetPerUnit:          number | null
  /** ready for the fields payload; null when there are no variable lines. */
  tareOverrides:          Record<string, number> | null
  /** human-readable weight/tare/underweight blockers; [] = safe on this axis. */
  blockers:               string[]
}

/** Fixed lines with no resolvable tare (neither line override nor stock_item).
 *  Variable lines are excluded — they aren't "missing", the operator weighs them.
 *
 *  ALSO catches a line whose `recipe_units` is 0 while carrying a REAL tare
 *  (audit 2026-08-17 P2-2). The tare maths multiplies by recipe_units, so such a
 *  line contributes 0 kg — the pallet's net comes out HIGHER than the truth and
 *  the grower is over-billed, with nothing anywhere saying so. It reads as a
 *  complete recipe, because tare_kg and tare_basis are both set.
 *
 *  ⚠️ The `> 0` on the tare is load-bearing, not defensive padding. Three ACTIVE
 *  kf49 recipe lines have recipe_units = 0 with tare 0.0000 — two on products
 *  with live pallets (D15D 125, E16D 40). Those contribute 0 kg whether or not
 *  the units are 0, so they are harmless; blocking them would have taken two
 *  live products offline the moment this shipped. Flag only the case where a
 *  non-zero tare is actually being thrown away. */
export const missingTareLinesOf = (components: TareComponent[]): string[] =>
  components
    .filter(c => !c.is_variable && (
      c.tare_kg == null || c.tare_basis == null ||
      (c.recipe_units <= 0 && c.tare_kg > 0)
    ))
    .map(c => c.item)

const numOrNull = (v: unknown): number | null => (v != null ? Number(v) : null)
const categoryNameOf = (sc: any): string | null =>
  ((Array.isArray(sc) ? sc[0] : sc)?.name ?? null) as string | null

/** One live `product_recipes` row (with its cost component's stock item) as a
 *  TareComponent — the single mapping the scale and the setup check share, so
 *  "this line has a tare" is read the same way in both. */
export function toTareComponent(r: any): TareComponent {
  const si = r.cost_components?.stock_items
  const lineTare = numOrNull(r.line_tare_weight_kg)
  return {
    component_id: String(r.component_id),
    recipe_units: Number(r.units_per_pallet),
    item:         (si?.item ?? r.cost_components?.name ?? '(unnamed line)') as string,
    tare_kg:      lineTare ?? numOrNull(si?.tare_weight_kg),
    tare_basis:   (r.line_tare_basis ?? si?.tare_basis ?? null) as TareBasis | null,
    // Variable-weight item (mig 869); a pinned line-tare override → fixed.
    is_variable:  si?.tare_variable === true && lineTare == null,
    category:     categoryNameOf(si?.stock_categories),
  }
}

/**
 * Can a pallet on this recipe EVER be weighed? — the question a weightless SHELL
 * has to answer at create time, which is NOT the question computePalletTotals
 * answers.
 *
 * computePalletTotals asks "can I compute a net RIGHT NOW", so it returns null
 * for a variable line the operator hasn't weighed yet. That is correct there and
 * wrong here: deferring the measured tare is the entire point of a shell. Using
 * it as a shell gate would block every recipe with a variable line.
 *
 * This asks only about defects the operator cannot fix at the scale — the ones
 * that make WeighPalletDialog permanently refuse the pallet it is handed:
 *   - no product resolved at all  → no recipe, no tare, ever
 *   - units_per_pallet unset/0    → `standard` is 0, so the tare maths can't scale
 *   - recipe has zero lines       → nothing to tare from
 *   - a FIXED line has no tare    → an admin must fix the rate card
 *
 * Pallet-create audit 2026-08-17 P1-1: the bulk shell path checked none of these
 * and minted three pallets on a pack code with no rate card. They sat
 * `awaiting_weight` for three weeks because every weigh attempt was refused,
 * having already burnt their pallet numbers and moved the run's rollup.
 */
export interface ShellWeighability {
  ok:     boolean
  /** operator-facing reason, ready to render; null when ok. */
  reason: string | null
}

export function shellWeighability(input: {
  /** true once the rate-card read has SUCCEEDED — never `variants.length > 0`. */
  rateCardLoaded: boolean
  /** active rate-card products for the pack code, BEFORE the variety fold. */
  allVariantCount: number
  /** …and after it. Zero here with a non-zero above = wrong-variety, not missing. */
  varietyVariantCount: number
  /** the resolved product, or null when the mark didn't pin one. */
  productId:       string | null
  /** units_per_pallet of the resolved product; 0 when unset. */
  standard:        number
  /** the resolved product's current recipe lines; null = not fetched yet. */
  components:      TareComponent[] | null
  packType:        string
  runVariety:      string
}): ShellWeighability {
  // Fail OPEN while the rate card is still loading or after a failed read — an
  // empty catalogue is what both look like. The caller blocks that window
  // separately (`ratecardUnresolved`); double-blocking here would just make the
  // reason wrong. Same rule as useVariantsForPack's `loaded`.
  if (!input.rateCardLoaded) return { ok: true, reason: null }

  if (input.allVariantCount === 0) {
    return {
      ok: false,
      reason: `There's no active rate card for pack code ${input.packType}, so the app can't resolve a tare — these pallets could never be weighed. Add the product under Financial → Rate Card, or fix the line's pack type.`,
    }
  }
  if (input.varietyVariantCount === 0) {
    return {
      ok: false,
      reason: `The catalogue has ${input.allVariantCount} ${input.packType} variant${input.allVariantCount === 1 ? '' : 's'}, but none is tagged for ${input.runVariety} — so no tare resolves for this run. Ask an admin to add a ${input.runVariety} variant, or pick a different line.`,
    }
  }
  // No product pinned yet — the caller's own variant gate owns that message.
  if (!input.productId) return { ok: true, reason: null }
  // Recipe not fetched yet — don't guess.
  if (input.components == null) return { ok: true, reason: null }

  if (input.components.length === 0) {
    return {
      ok: false,
      reason: `The ${input.packType} rate card has no recipe lines, so there's no tare to weigh against. Add them under Financial → Rate Card before creating these pallets.`,
    }
  }
  if (!input.standard || input.standard <= 0) {
    return {
      ok: false,
      reason: `The ${input.packType} rate card has no "units per pallet" set, so the tare can't be scaled to a carton count. Set it under Financial → Rate Card before creating these pallets.`,
    }
  }
  const missing = missingTareLinesOf(input.components)
  if (missing.length > 0) {
    return {
      ok: false,
      reason: `Tare isn't configured on ${missing.length} ${input.packType} rate-card line${missing.length === 1 ? '' : 's'} (${missing.join(', ')}), so these pallets could never be weighed. Set a tare on every line under Financial → Rate Card.`,
    }
  }
  return { ok: true, reason: null }
}

// ---------------------------------------------------------------------------
// Blocker builders.
//
// Both are written as FLAT lists of (condition → message) rather than nested
// if/else chains: the nesting was what pushed computePalletTotals over the
// complexity gate, and a reader wants to see the set of reasons, not a
// decision tree. `dropNulls` keeps the pairs readable inline.
// ---------------------------------------------------------------------------

const dropNulls = <T = string>(xs: (T | null)[]): T[] =>
  xs.filter((x): x is T => x !== null)

// The weight/tare/underweight half, split out of computePalletTotals: ~40 lines
// of message shaping inside a function whose job is arithmetic. Module-private
// — computePalletTotals is the only caller, and the create form deliberately
// keeps its own strings (see newPalletFormBlockers).
interface BlockerInput {
  standard:               number
  components:             TareComponent[]
  cartonQty:              number
  gross:                  number
  /** false on a flow that doesn't weigh (commodity_templates.tracks_pallet_weight). */
  weightRequired:         boolean
  cardLabel?:             string | null
  totalTareKg:            number | null
  isUnderweight:          boolean
  missingTareLines:       string[]
  unenteredVariableLines: string[]
}

function weightAndTareBlockers(i: BlockerInput): string[] {
  // `weightless` = a flow that doesn't weigh, with nothing typed in. Cartons are
  // still required (that IS the berry unit of sale) — only the weight and the
  // tare it would have been reduced by fall away.
  const weightless = !i.weightRequired && i.gross <= 0
  return dropNulls([
    i.weightRequired && i.gross <= 0 ? 'Enter gross weight' : null,
    i.cartonQty < 1 ? 'Enter carton count' : null,
    ...(weightless ? [] : recipeBlockers(i)),
    i.isUnderweight ? 'Net per carton is below the minimum' : null,
  ])
}

// "Weigh this" and "the card is broken" can BOTH apply — an unweighed variable
// line is also why the tare total is null — so this returns a list, not a first
// match.
function recipeBlockers(i: BlockerInput): (string | null)[] {
  const card = i.cardLabel?.trim()
  const tare = i.missingTareLines.length > 0
    ? (card
        ? `Tare not configured on ${card}: ${i.missingTareLines.join(', ')}`
        : `Tare not configured: ${i.missingTareLines.join(', ')}`)
    : (i.totalTareKg == null && i.unenteredVariableLines.length === 0
        ? incompleteRecipeBlocker(i.standard, i.components.length, card)
        : null)
  return [
    i.unenteredVariableLines.length > 0 ? `Weigh: ${i.unenteredVariableLines.join(', ')}` : null,
    tare,
  ]
}

// Name the field that is actually missing (audit 2026-08-17 P2-1). "Recipe
// incomplete" sent admins to the recipe LINES, where every tare was already
// set — the real gap is `units_per_pallet` on the product HEADER, a different
// screen. A blocker that points at the wrong place costs more than one that
// says nothing.
function incompleteRecipeBlocker(
  standard: number, componentCount: number, card: string | undefined,
): string {
  const where = card ? ` on ${card}` : ''
  if (!standard || standard <= 0) {
    return `No "units per pallet" set${where} — the tare can't be scaled to a carton count. `
         + 'Set it on the product itself (Financial → Rate Card), not on the recipe lines.'
  }
  if (componentCount === 0) {
    return `No recipe lines${where} — there's nothing to work the tare out from. `
         + 'Add them under Financial → Rate Card.'
  }
  return card
    ? `${card} is incomplete — ask admin to finish the rate card`
    : 'Recipe incomplete — ask admin to finish the rate card'
}

// The create form's FULL pre-submit blocker list, lifted VERBATIM out of
// NewPalletForm — same conditions, same strings, same order.
//
// Pure and exported for two reasons. It was an inline IIFE whose only coverage
// was the page's render tests (CRAP 342, the worst score in the file), and the
// create BUTTON kept a second hand-maintained copy of the same conditions. The
// button now derives its disabled state from this list, so the comment that
// always claimed "the two never drift" is true by construction.
//
// Deliberately NOT reusing the strings above: the form says "Tare not
// configured: X" without the rate-card name (its own panel shows that) and
// distinguishes an UNLOADED rate card — "tap Retry" — from an unfinished one.
// Those are different operator actions.
export interface FormBlockerInput {
  palletType:             string
  packType:               string
  weightRequired:         boolean
  gross:                  number
  cartonQty:              number
  /** rate-card variants for this pack+variety; >0 with no productId = unresolved. */
  variantCount:           number
  productId:              string | null
  /** pack lines still missing an operator stock pick. */
  unpickedPackLines:      number
  /** nothing weighed on a flow that doesn't weigh → the tare set stands down. */
  weightlessSave:         boolean
  missingTareLines:       string[]
  unenteredVariableLines: string[]
  totalTareKg:            number | null
  /** the rate card hasn't loaded yet — send the operator to Retry, not to admin. */
  ratecardUnresolved:     boolean
  isUnderweight:          boolean
}

/** The form section a blocker belongs to — the create form marks that section
 *  red and jumps to it when Create is tapped (see useFieldIssues). */
export type PalletFormField = 'palletType' | 'gross' | 'cartons' | 'packType' | 'variant' | 'stock' | 'tare'
export interface FormBlocker { field?: PalletFormField; message: string }

export function newPalletFormBlockers(i: FormBlockerInput): string[] {
  return newPalletFormBlockerItems(i).map(b => b.message)
}

export function newPalletFormBlockerItems(i: FormBlockerInput): FormBlocker[] {
  return dropNulls<FormBlocker>([
    ...formSpecBlockers(i),
    ...(i.weightlessSave ? [] : formRecipeBlockers(i)),
    i.isUnderweight ? { field: 'gross', message: 'Net per carton is below the minimum' } : null,
  ])
}

// The spec fields the form itself owns — nothing here depends on the recipe.
function formSpecBlockers(i: FormBlockerInput): (FormBlocker | null)[] {
  return [
    !i.palletType ? { field: 'palletType', message: 'Pick a pallet type' } : null,
    i.weightRequired && i.gross <= 0 ? { field: 'gross', message: 'Enter gross weight' } : null,
    i.cartonQty < 1 ? { field: 'cartons', message: 'Enter carton count' } : null,
    !i.packType.trim() ? { field: 'packType', message: 'Pick a pack type' } : null,
    // Variant must resolve when the rate card has options for this pack+variety
    // — else billing falls back to the cheapest variant. Was checked only at
    // save (an unblocked Create that then threw).
    i.variantCount > 0 && !i.productId ? { field: 'variant', message: 'Pick a rate-card variant for the Mark' } : null,
    i.unpickedPackLines > 0 ? { field: 'stock', message: 'Pick a stock item for each pack line' } : null,
  ]
}

function formRecipeBlockers(i: FormBlockerInput): (FormBlocker | null)[] {
  // An unloaded rate card yields the same empty recipe as an unfinished one (no
  // variant → useTareRecipe is disabled → no components), so send the operator
  // to the Retry above instead of to the admin. No field: these are fixed in
  // the rate card, not on this form.
  const tare = i.missingTareLines.length > 0
    ? `Tare not configured: ${i.missingTareLines.join(', ')}`
    : (i.totalTareKg == null && i.unenteredVariableLines.length === 0
        ? (i.ratecardUnresolved
            ? 'Rate card hasn’t loaded — tap Retry above'
            : 'Recipe incomplete — ask admin to finish the rate card')
        : null)
  return [
    i.unenteredVariableLines.length > 0 ? { field: 'tare', message: `Weigh: ${i.unenteredVariableLines.join(', ')}` } : null,
    tare ? { message: tare } : null,
  ]
}

export function computePalletTotals(input: PalletComputeInput): PalletComputeResult {
  const { standard, components, cartonQty, gross, tareInputs, variant } = input

  const variableComponents = components.filter(c => c.is_variable)

  // Fixed lines missing a tare (neither line override nor stock_item). These
  // BLOCK — no silent guess. Variable lines are excluded (they're not "missing",
  // the operator enters them). Shared with shellWeighability so "a line has no
  // tare" has exactly one definition.
  const missingTareLines = missingTareLinesOf(components)

  // Variable lines not weighed yet (empty / negative / non-finite input).
  const unenteredVariableLines = variableComponents
    .filter(c => { const v = parseFloat(tareInputs[c.component_id] ?? ''); return !Number.isFinite(v) || v < 0 })
    .map(c => c.item)

  // Total tare from the recipe. Null when incomplete (a fixed line missing tare,
  // or a variable line not yet weighed) — caller refuses to save until resolved.
  const recipeTareKg = (() => {
    if (!standard || cartonQty <= 0) return null
    if (components.length === 0) return null
    if (missingTareLines.length > 0) return null
    if (unenteredVariableLines.length > 0) return null
    let total = 0
    for (const c of components) {
      const perUnit = c.is_variable ? parseFloat(tareInputs[c.component_id] ?? '') : c.tare_kg
      if (perUnit == null || !Number.isFinite(perUnit)) continue
      const basis = c.tare_basis ?? (c.is_variable ? 'PER_PALLET' : null)
      if (tareScalesWithUnits(basis)) {
        total += c.recipe_units * perUnit * (cartonQty / standard)
      } else if (basis === 'PER_PALLET') {
        total += c.recipe_units * perUnit
      }
    }
    return total
  })()

  const totalTareKg = recipeTareKg
  const netWeight   = recipeTareKg != null && gross > 0 ? gross - recipeTareKg : null

  const minPerUnit    = variant?.min_weight_per_unit_kg ?? null
  const targetPerUnit = variant?.target_weight_per_unit_kg ?? null
  const netPerCarton  = (netWeight != null && cartonQty > 0) ? netWeight / cartonQty : null
  const isUnderweight = minPerUnit != null && netPerCarton != null && netPerCarton < minPerUnit

  const tareOverrides = variableComponents.length > 0
    ? Object.fromEntries(variableComponents.map(c => [c.component_id, parseFloat(tareInputs[c.component_id] ?? '')]))
    : null

  // Weight/tare/underweight blockers — mirrors the form's pre-submit panel.
  // Field blockers (pallet type, pack type, pack-line stock pick) stay with the
  // caller; this covers only what depends on weight + the recipe. Extracted so
  // this function stays about the ARITHMETIC — the message-shaping branches
  // were most of its complexity and none of its maths.
  const blockers = weightAndTareBlockers({
    standard, components, cartonQty, gross,
    weightRequired: input.weightRequired ?? true,
    cardLabel: input.cardLabel,
    totalTareKg, isUnderweight, missingTareLines, unenteredVariableLines,
  })


  return {
    totalTareKg, netWeight, netPerCarton,
    missingTareLines, unenteredVariableLines,
    isUnderweight, minPerUnit, targetPerUnit,
    tareOverrides, blockers,
  }
}
